import { AiCreditTooltip } from '../../components/ui/AiCreditTooltip';
import { DialogContent } from '../../components/ui/DialogContent';
import {
  Alert,
  Button,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { ResponsiveDialog } from '../../components/ui/ResponsiveDialog';
import {
  captureDraftFields,
  capturePhoto,
  RapidCaptureRecord,
} from './rapidCaptureTypes';
import { useFeedback } from '../feedback/FeedbackProvider';
import { Item } from '../../domain/item';
import { captureActivityPresentation } from './captureActivityPresentation';
import { CaptureActivityThumbnail } from './CaptureActivityThumbnail';

export function RapidCaptureQueueDialog({
  open,
  records,
  items,
  online,
  onClose,
  onRetry,
  onDiscard,
  onDismissAI,
  onOpenItem,
  onEditDraft,
}: {
  open: boolean;
  records: RapidCaptureRecord[];
  items: Item[];
  online: boolean;
  onClose: () => void;
  onRetry: (operationId: string) => Promise<void>;
  onDiscard: (operationId: string) => Promise<void>;
  onDismissAI?: (operationId: string) => Promise<void>;
  onOpenItem: (item: Item) => void;
  onEditDraft?: (operationId: string) => void;
}) {
  const { confirm, notify } = useFeedback();
  const itemsById = new Map(items.map((item) => [item.id, item]));
  const run = async (action: () => Promise<void>) => {
    try {
      await action();
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : 'This action could not finish. Your capture is retained.',
        'error',
      );
    }
  };
  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      aria-labelledby="rapid-queue-title"
    >
      <DialogHeader
        id="rapid-queue-title"
        title="Capture queue"
        onClose={onClose}
      />
      <DialogContent>
        {!records.length ? (
          <Typography sx={{ py: 3 }}>There is no capture activity.</Typography>
        ) : (
          <List disablePadding>
            {records.map((record) => {
              const item = itemsById.get(record.itemId);
              const fields = captureDraftFields(record, item);
              const presentation = captureActivityPresentation(
                record,
                online,
                item,
              );
              return (
                <ListItem
                  key={record.operationId}
                  divider
                  alignItems="flex-start"
                  sx={{ gap: 1.5, px: 0, py: 1.5, flexWrap: 'wrap' }}
                >
                  <CaptureActivityThumbnail
                    image={capturePhoto(record)?.image ?? new Blob([])}
                    alt={`Captured item: ${fields.keywords.trim() || 'Unnamed'}`}
                  />
                  <ListItemText
                    primary={fields.keywords.trim() || 'Unnamed'}
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color={
                            presentation.severity === 'info'
                              ? 'text.secondary'
                              : `${presentation.severity}.main`
                          }
                        >
                          {presentation.label}
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          {fields.location}
                        </Typography>
                        {presentation.detail && (
                          <Typography
                            component="span"
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            {presentation.detail}
                          </Typography>
                        )}
                      </>
                    }
                    sx={{ my: 0, minWidth: 0 }}
                  />
                  <Stack spacing={0.25} alignItems="flex-end">
                    {record.schemaVersion === 6 &&
                      record.state !== 'terminal' &&
                      !record.itemUnavailable &&
                      onEditDraft && (
                        <Button
                          size="small"
                          onClick={() => onEditDraft(record.operationId)}
                        >
                          Edit details
                        </Button>
                      )}
                    {presentation.canOpenItem &&
                      item &&
                      (!onEditDraft ||
                        record.aiSkipReason === 'cost-unconfirmed') && (
                        <Button size="small" onClick={() => onOpenItem(item)}>
                          Open item
                        </Button>
                      )}
                    {presentation.canRetry && (
                      <Stack direction="row" alignItems="center">
                        <Button
                          size="small"
                          onClick={() => {
                            void run(async () => {
                              if (
                                record.schemaVersion !== 6 &&
                                !(await confirm(
                                  'Check whether this item was saved, then recover its photo if needed? Existing paid AI work will not be replayed.',
                                  'Recover older capture',
                                ))
                              )
                                return;
                              if (
                                record.lastError?.phase === 'edits' &&
                                record.lastError.category === 'conflict' &&
                                onEditDraft
                              ) {
                                onEditDraft(record.operationId);
                                return;
                              }
                              await onRetry(record.operationId);
                            });
                          }}
                        >
                          {record.lastError?.phase === 'edits' &&
                          record.lastError.category === 'conflict'
                            ? 'Review changes'
                            : presentation.retryLabel || 'Retry'}
                        </Button>
                        {record.itemCreated &&
                          record.analyzeImage &&
                          record.lastError?.phase !== 'edits' && (
                            <AiCreditTooltip
                              authority={
                                online
                                  ? {
                                      userId: record.userId,
                                      collectionId: record.collectionId,
                                      membershipVersion:
                                        record.membershipVersion,
                                      compatibilityMode:
                                        record.compatibilityMode,
                                    }
                                  : undefined
                              }
                              showPreviouslyAnalysedNote={false}
                            />
                          )}
                      </Stack>
                    )}
                    {presentation.canDismissAI && onDismissAI && (
                      <Button
                        size="small"
                        disabled={!online}
                        onClick={() => {
                          void run(() => onDismissAI(record.operationId));
                        }}
                      >
                        Dismiss
                      </Button>
                    )}
                    {presentation.canDiscard && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          void run(async () => {
                            if (
                              await confirm(
                                'Discard pending changes? Local changes will be removed. Changes already saved to the server remain saved; the item is never deleted.',
                                'Discard pending changes',
                              )
                            )
                              await onDiscard(record.operationId);
                          });
                        }}
                      >
                        Discard
                      </Button>
                    )}
                  </Stack>
                </ListItem>
              );
            })}
          </List>
        )}
        {records.some(
          (record) =>
            captureActivityPresentation(record, online).severity !== 'info',
        ) && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Captures that need attention stay here until you resolve or dismiss
            them. Saved items remain in inventory.
          </Alert>
        )}
      </DialogContent>
    </ResponsiveDialog>
  );
}
